import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  CreatePinModel,
  LoginModel,
  LoginResponseModel,
  ResetPinModel,
  SendOTPModel,
  ValidateOTPModel,
  ValidatePinModel,
} from "model/auth-model";
import { ApiResponse } from "model/response-model";
import { AuthState, getToken } from "redux/authSlice";
import { JwtPayload, jwtDecode } from "jwt-decode";
import {
  ActivitiesModel,
  ActivitiesQuery,
  ActivitiesStatModel,
  CountriesPercentageModel,
  DashboardQuery,
  PerformingCountriesModel,
  ProjectCompanyPagination,
  ProjectCountModel,
  ProjectPerformanceModel,
  ProjectQuery,
  SubCompanyHistory,
  SubCompanyHistoryPagination,
  SubCompanyPagination,
  SubscriptionCompany,
  SubscriptionCount,
  SubscriptionPercentage,
  SubscriptionRevenue,
  SubscriptionSummaryTotal,
} from "model/dashboardModel";
import { prepareHeaders } from "./defaultHeader";
import { baseQueryWithReauth } from "./config/interceptor";

const basePath = "Dashboard";

// Define a service using a base URL and expected endpoints
export const dashboardApi = createApi({
  reducerPath: "dashboardApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Dashboard"],
  endpoints: (builder) => ({
    getSubscriptionSummary: builder.query<
      ApiResponse<SubscriptionSummaryTotal>,
      DashboardQuery
    >({
      query: (query) => ({
        url: `${basePath}/subscription/summary`,
        params: query,
      }),
    }),
    getAllSubscriptionSummary: builder.query<
      ApiResponse<SubscriptionSummaryTotal[]>,
      void
    >({
      query: () => ({
        url: `${basePath}/subscription/all-summary`,
      }),
    }),
    getSubscriptionRevenue: builder.query<
      ApiResponse<SubscriptionRevenue[]>,
      DashboardQuery
    >({
      query: (query) => ({
        url: `${basePath}/subscription/revenue`,
        params: query,
      }),
    }),
    getSubscriptionPercentage: builder.query<
      ApiResponse<SubscriptionPercentage>,
      DashboardQuery
    >({
      query: (query) => ({
        url: `${basePath}/subscription/percentage`,
        params: query,
      }),
    }),
    getSubscriptionCompany: builder.query<
      ApiResponse<SubCompanyPagination>,
      DashboardQuery
    >({
      query: (query) => ({
        url: `${basePath}/subscription/company`,
        params: query,
      }),
    }),
    getSubscriptionCompanyHistory: builder.query<
      ApiResponse<SubCompanyHistory[]>,
      DashboardQuery
    >({
      query: (query) => ({
        url: `${basePath}/subscription/company/history`,
        params: query,
      }),
    }),
    getProjectPerformance: builder.query<
      ApiResponse<ProjectPerformanceModel[]>,
      void
    >({
      query: () => ({
        url: `${basePath}/project/performance`,
      }),
    }),
    getProjectCount: builder.query<ApiResponse<ProjectCountModel[]>, void>({
      query: () => ({
        url: `${basePath}/project/count`,
      }),
    }),
    getProjectCompany: builder.query<
      ApiResponse<ProjectCompanyPagination>,
      ProjectQuery
    >({
      query: (query) => ({
        url: `${basePath}/project/company`,
        params: query,
      }),
    }),
    getPerformingCountries: builder.query<
      ApiResponse<PerformingCountriesModel[]>,
      void
    >({
      query: () => ({
        url: `${basePath}/country/performingcountries`,
      }),
    }),
    getCountriesPercentage: builder.query<
      ApiResponse<CountriesPercentageModel[]>,
      void
    >({
      query: () => ({
        url: `${basePath}/country/countriespercentage`,
      }),
    }),
    getActivitiesSummary: builder.query<
      ApiResponse<ActivitiesModel>,
      ActivitiesQuery
    >({
      query: (ActivitiesQuery) => ({
        url: `${basePath}/activities/summary`,
        params: ActivitiesQuery,
      }),
    }),
    getActivitiesStatistics: builder.query<
      ApiResponse<ActivitiesStatModel[]>,
      any
    >({
      query: (application) => ({
        url: `${basePath}/activities/statistics`,
        params: application,
      }),
    }),
    // deleteCompany: builder.mutation<ApiResponse<any>, any>({
    //   query: (subdomain) => ({
    //     url: `https://api.jobpro.app/api/tenant/deletetenant/${subdomain}`,
    //     method: 'DELETE',
    //     headers: {'subdomain': subdomain}
    //   }),
    // }),
  }),
});

// Export the auto-generated hooks
export const {
  useGetSubscriptionSummaryQuery,
  useGetAllSubscriptionSummaryQuery,
  useGetSubscriptionRevenueQuery,
  useGetSubscriptionPercentageQuery,
  useGetSubscriptionCompanyQuery,
  useGetSubscriptionCompanyHistoryQuery,
  useGetProjectCompanyQuery,
  useGetProjectCountQuery,
  useGetProjectPerformanceQuery,
  useGetCountriesPercentageQuery,
  useGetPerformingCountriesQuery,
  useGetActivitiesStatisticsQuery,
  useGetActivitiesSummaryQuery,
  // useDeleteCompanyMutation,
} = dashboardApi;
